import { Action } from "@ciaopeople/cp-shatsu/EventHandler/event-handler";
import { inEnum, isDefined, isNumber, isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { ExecutionId } from "../player/MediaResource.js";
import { hasOwnProperty } from "@ciaopeople/cp-shatsu/Utils";
import { isVideoInfoRawData } from "../../model/video/VideoInfo.js";
import { isMediaId, MediaId, MediaInfoRawData } from "../../model/MediaInfo.js";
import { isAudioInfoRawData } from "../../model/audio/AudioInfo.js";

export enum MediaEventType {
    PLAYLIST_STARTED = "playlist_started",
    MEDIA_STARTED = "media_started",
    MEDIA_PAUSED = "media_paused",
    MEDIA_RESUMED = "media_resumed",
    MEDIA_LOAD_STARTED = "load_started",
    MEDIA_LOADED = "media_loaded",
    MEDIA_IMPRESSION = "media_impression",
    MEDIA_PROGRESS = "media_progress",
    MEDIA_COMPLETE = "media_complete",
    PLAYLIST_ENDED = "playlist_ended",
    MEDIA_BUFFERING = "media_buffering",
    MEDIA_BUFFER_END = "media_buffer_end"
}


export enum MediaControlOrigin {

    USER = "user",
    NON_USER = "user",
    ADV = "adv"

}

export interface PlayerEvent {

    mediaId: MediaId,
    executionId?: ExecutionId,
    playlistId?: string,
    eventType: any
    timing?: EventTiming
}


export interface EventTiming {
    duration?: number,
    timeOrigin: number
}

export function isEventTiming(a: any): a is EventTiming {
    return (!("duration" in a) || isNumber(a.duration)) &&
        ("timeOrigin" in a && isNumber(a.timeOrigin));
}

export interface MediaEvent<MI extends MediaInfoRawData<any>> extends PlayerEvent {
    eventType: MediaEventType
    info: MI
    executionId: ExecutionId
}


export function isPlayerEvent(a: any): a is PlayerEvent {
    return isObject(a) &&
        ("mediaId" in a && isMediaId(a.mediaId)) &&
        (!("playlistId" in a) || isString(a.playlistId)) &&
        ("eventType" in a) &&
        (!("timing" in a) || isEventTiming(a.timing)) &&
        (!("executionId" in a) || isString(a.executionId))

}

export function isMediaEvent(a: any): a is MediaEvent<any> {
    return isPlayerEvent(a) &&
        ("eventType" in a && inEnum(a.eventType, MediaEventType)) && 
        ("info" in a && (isVideoInfoRawData(a["info"]) || isAudioInfoRawData(a["info"]))) &&
        ("executionId" in a);
}

function isMediaEventOfType(a: any, t: MediaEventType): a is MediaEvent<any> {
    return isMediaEvent(a) && a.eventType == t;
}


export interface EventTimingWithDuration extends EventTiming {
    duration: number
}


export function isEventTimingWithDuration(a: any): a is EventTimingWithDuration {
    return isEventTiming(a) && ("duration" in a);
}


export interface MediaEventWithTiming extends MediaEvent<any> {
    timing: EventTiming
}





export function isMediaEventWithTiming(a: any): a is MediaEventWithTiming {
    return isMediaEvent(a) && ('timing' in a);
}



export interface MediaEventWithCurrentTime extends MediaEvent<any> {
    currentTime: number;
}


export function isMediaEventWithCurrentTime(x: any): x is MediaEventWithCurrentTime  {
    return isMediaEvent(x) && ("currentTime" in x && isNumber(x["currentTime"]))
}

export function isMediaEventWithCurrentTimeOfType(x: any, t: MediaEventType): x is MediaEventWithCurrentTime {
    return isMediaEventWithCurrentTime(x) && x.eventType == t;
}




export interface MediaStartedEvent extends MediaEventWithTiming {
    timing: EventTimingWithDuration
    eventType: MediaEventType.MEDIA_STARTED
}


export function isMediaStartedEvent(a: any): a is MediaStartedEvent {
    return isMediaEventOfType(a, MediaEventType.MEDIA_STARTED) &&
        isEventTimingWithDuration(a.timing);
}



export interface MediaLoadedEvent extends MediaEvent<any> {
    metadata: {
        duration: number
    }
    eventType: MediaEventType.MEDIA_LOADED,
    genuine: boolean
}

export function isMediaLoadedEvent(a: any): a is MediaLoadedEvent {
    return isMediaEventOfType(a, MediaEventType.MEDIA_LOADED) &&
        ("metadata" in a && isObject(a["metadata"]) &&
            "duration" in a["metadata"] && isNumber(a["metadata"]["duration"]));
}





export interface MediaPausedEvent extends MediaEventWithCurrentTime {
    eventType: MediaEventType.MEDIA_PAUSED,
    origin: MediaControlOrigin
    
}



export function isMediaPausedEvent(x: any): x is MediaPausedEvent {
    return isMediaEventWithCurrentTimeOfType(x,MediaEventType.MEDIA_PAUSED)
        && (hasOwnProperty(x, "origin") && inEnum(x["origin"],MediaControlOrigin));
}


export interface MediaResumedEvent extends MediaEventWithCurrentTime {
    eventType: MediaEventType.MEDIA_RESUMED,
    origin: MediaControlOrigin
}

export function isMediaResumedEvent(x: any): x is MediaResumedEvent {
    return isMediaEventWithCurrentTimeOfType(x,MediaEventType.MEDIA_RESUMED) &&
            (hasOwnProperty(x, "origin") && inEnum(x["origin"],MediaControlOrigin));
}

export interface MediaLoadStartedEvent extends MediaEventWithTiming {
    eventType: MediaEventType.MEDIA_LOAD_STARTED
}

export function isMediaLoadStartedEvent(a: any): a is MediaLoadStartedEvent {
    return isMediaEventOfType(a, MediaEventType.MEDIA_LOAD_STARTED) &&
        ('timing' in a);
}

export interface MediaProgressEvent extends MediaEventWithCurrentTime {
    progress: number,
    eventType: MediaEventType.MEDIA_PROGRESS
}

export function isMediaProgressEvent(a: any): a is MediaProgressEvent {
    return ("progress" in a && isNumber(a.progress)) &&
           isMediaEventWithCurrentTimeOfType(a, MediaEventType.MEDIA_PROGRESS);

}

export interface MediaImpressionEvent extends MediaEvent<any> {
    eventType: MediaEventType.MEDIA_IMPRESSION
}


export function isMediaImpressionEvent(a: any): a is MediaImpressionEvent {
    return isMediaEventOfType(a, MediaEventType.MEDIA_IMPRESSION);
}


export interface MediaPlaylistStartedEvent extends MediaEvent<any> {
    eventType: MediaEventType.PLAYLIST_STARTED,
    playlistId: string
}



export function isMediaPlaylistStartedEvent(a: any): a is MediaPlaylistStartedEvent {
    return isMediaEventOfType(a, MediaEventType.PLAYLIST_STARTED) &&
                isDefined(a.playlistId);
}

export interface MediaPlaylistEndedEvent extends MediaEvent<any> {
    eventType: MediaEventType.PLAYLIST_ENDED,
    playlistId: string
}



export function isMediaPlaylistEndedEvent(a: any): a is MediaPlaylistStartedEvent {
    return isMediaEventOfType(a, MediaEventType.PLAYLIST_ENDED) &&
                isDefined(a.playlistId);
}

export interface MediaCompleteEvent extends MediaEvent<any> {
    eventType: MediaEventType.MEDIA_COMPLETE
}

export function isMediaCompleteEvent(a: any): a is MediaCompleteEvent {
    return isMediaEventOfType(a, MediaEventType.MEDIA_COMPLETE);
}

export interface MediaBufferingEvent extends MediaEventWithCurrentTime {
    eventType: MediaEventType.MEDIA_BUFFERING
}

export function isMediaBufferingEvent(a: any): a is MediaBufferingEvent {
    return isMediaEventWithCurrentTimeOfType(a, MediaEventType.MEDIA_BUFFERING);
}

export interface MediaBufferEndEvent extends MediaEventWithCurrentTime {
    eventType: MediaEventType.MEDIA_BUFFER_END
}

export function isMediaBufferEndEvent(a: any): a is MediaBufferEndEvent {
    return isMediaEventWithCurrentTimeOfType(a, MediaEventType.MEDIA_BUFFER_END);
}


export type MediaEventListener = (evt: MediaEvent<any>) => void;

export type MediaEventAction = Action

export interface MediaEventsSource {

    onPlayerEvent(listener: MediaEventListener): MediaEventAction;
    offPlayerEvent(listener: MediaEventAction): void;

}
